"use client";

import { Flex, FlexColumn } from "@/components/Flex";
import br from "@/styles/breakpoints.module.css";
import { spacing } from "@/utils/spacing";
import { themeColors } from "@/utils/themeColors";
import type { UserDetails } from "@knowt/syncing/graphql/schema";
import {
    ExtraActionEnum,
    KNOWT_GAMIFICATION_STORAGE,
    STREAK_ACTION_COLORS,
} from "@knowt/syncing/hooks/gamification/constants";
import { useCurrentUser } from "@knowt/syncing/hooks/user/useCurrentUser";
import { useRouter } from "@bprogress/next";
import { Suspense } from "react";
import UserProgressDetailsCard from "./UserProgressDetailsCard";
import StreakIcon from "@/components/icons/StreakIcon";
import dynamic from "next/dynamic";
import XpIcon from "@/components/icons/XpIcon";
import XPAndLevelSummary, { NonOwnerXPAndLevelSummary } from "@/features/Gamification/components/XPAndLevelSummary";
import { useGamificationProgressingContextSelector } from "@/features/Gamification/Progressing/GamificationProgressingContext";

const FeaturedBadgeProgress = dynamic(() => import("./FeaturedBadgeProgress"), { ssr: false });

type ViewProps = {
    ownerDetails: UserDetails;
};

const OwnerView = () => {
    const { user } = useCurrentUser();
    const router = useRouter();

    const openStreakStatusPopup = useGamificationProgressingContextSelector(state => state.openStreakStatusPopup);

    if (!user) {
        return null;
    }

    return (
        <FlexColumn className={br.mdDownWidth100} style={{ gap: spacing.MD }}>
            <Flex className={br.mdDownColumn} style={{ gap: "inherit", justifyContent: "space-between" }}>
                <XPAndLevelSummary
                    style={{
                        backgroundColor: themeColors.neutralWhite,
                        padding: `${spacing.LG} ${spacing.MD}`,
                        borderRadius: "2rem",
                        height: "100%",
                        flex: 2,
                    }}
                    titleProps={{
                        className: "bodyBold2",
                    }}
                    progressBarProps={{
                        height: "1.5rem",
                    }}
                />
                <Flex style={{ gap: "inherit" }}>
                    <UserProgressDetailsCard
                        title={"Streak"}
                        titleProps={{
                            className: "bodyBold2",
                        }}
                        customIcon={<StreakIcon size={34} />}
                        color={STREAK_ACTION_COLORS[ExtraActionEnum.STREAK]}
                        progress={(user.streak || 0).toString()}
                        onClick={openStreakStatusPopup}
                        containerStyle={{ flex: 1, height: "100%" }}
                    />
                    <UserProgressDetailsCard
                        title={"Coins"}
                        titleProps={{
                            className: "bodyBold2",
                        }}
                        icon={`${KNOWT_GAMIFICATION_STORAGE}/coin-icon.svg`}
                        iconProps={{
                            width: 34,
                            height: 34,
                        }}
                        color={STREAK_ACTION_COLORS[ExtraActionEnum.COINS]}
                        progress={(user.coins || 0).toString()}
                        onClick={() => router.push("/store")}
                        containerStyle={{ flex: 1, height: "100%" }}
                    />
                </Flex>
            </Flex>
            <FeaturedBadgeProgress variant="vertical" user={user} />
        </FlexColumn>
    );
};

const VisitorView = ({ ownerDetails }: ViewProps) => (
    <FlexColumn style={{ gap: spacing.MD }}>
        <Flex className={br.mdDownColumn} style={{ gap: spacing.MD, flex: 1 }}>
            <NonOwnerXPAndLevelSummary
                xp={ownerDetails.xp || 0}
                style={{
                    backgroundColor: themeColors.neutralWhite,
                    padding: `${spacing.LG} ${spacing.MD}`,
                    borderRadius: "2rem",
                    height: "100%",
                    flex: 2,
                    minWidth: "auto",
                }}
                titleProps={{
                    className: "bodyBold2",
                }}
                progressBarProps={{
                    height: "1.5rem",
                }}
            />
            <Flex style={{ gap: "inherit" }}>
                <UserProgressDetailsCard
                    title={"Streak"}
                    titleProps={{
                        className: "bodyBold2",
                    }}
                    customIcon={<StreakIcon size={34} />}
                    color={themeColors.streakDark}
                    progress={(ownerDetails.streak || 0).toString()}
                    containerStyle={{ flex: 1, cursor: "auto" }}
                />
                <UserProgressDetailsCard
                    title={"XP"}
                    titleProps={{
                        className: "bodyBold2",
                    }}
                    customIcon={<XpIcon size={34} />}
                    color={"#28C3E1"}
                    progress={(ownerDetails.xp || 0).toString()}
                    containerStyle={{ flex: 1, cursor: "auto" }}
                />
            </Flex>
        </Flex>
        <Suspense>
            <FeaturedBadgeProgress variant="vertical" user={ownerDetails} />
        </Suspense>
    </FlexColumn>
);

const ProfileGamificationCards = ({ ownerDetails, isOwner }: { ownerDetails: UserDetails; isOwner: boolean }) =>
    isOwner ? <OwnerView /> : <VisitorView ownerDetails={ownerDetails} />;

export default ProfileGamificationCards;
